/**
 * Bridge between Clerk's React `useAuth().getToken` and the non-React axios instance.
 *
 * A ClerkTokenBridge component (inside ClerkProvider) registers the token getter and marks
 * Clerk "ready" once loaded. The axios request interceptor awaits readiness, then attaches the
 * Clerk session token as a Bearer. If there's no Clerk session (legacy user, or no key), the
 * getter returns null and requests fall back to the legacy cookie auth — the parallel-run path.
 */
type TokenGetter = () => Promise<string | null>;

// Never block requests longer than this waiting for Clerk to load. If Clerk is slow or fails,
// requests proceed without a token (falling back to legacy cookie auth) rather than hanging.
const READY_TIMEOUT_MS = 4000;

let tokenGetter: TokenGetter | null = null;
let ready = false;
let resolveReady: () => void = () => undefined;
const readyPromise = new Promise<void>((resolve) => {
  resolveReady = resolve;
});

const readyTimer = setTimeout(() => markClerkReady(), READY_TIMEOUT_MS);

export const setClerkTokenGetter = (getter: TokenGetter | null) => {
  tokenGetter = getter;
};

export const markClerkReady = () => {
  if (!ready) {
    ready = true;
    clearTimeout(readyTimer);
    resolveReady();
  }
};

const waitForClerkReady = () => (ready ? Promise.resolve() : readyPromise);

export const getClerkToken = async (): Promise<string | null> => {
  await waitForClerkReady();
  if (!tokenGetter) return null;

  try {
    return await tokenGetter();
  } catch {
    return null;
  }
};
